import { useState } from "react";
import { PDFDocument, degrees } from "pdf-lib";
import { toast } from "sonner";
import { RotateCw, Trash2, ArrowLeft, ArrowRight, Loader2, MoveVertical, Download } from "lucide-react";
import { Seo } from "@/components/Seo";
import { GenericToolShell } from "@/components/GenericToolShell";
import { FileDrop } from "@/components/crop/FileDrop";
import { Button } from "@/components/ui/button";
import pdfjs from "@/lib/crop/pdfjsSetup";

export default function RearrangePdf() {
  const [srcBytes, setSrcBytes] = useState(null);
  const [pages, setPages] = useState([]); // {orig, rotation, thumb}
  const [busy, setBusy] = useState(false);
  const [fileMeta, setFileMeta] = useState(null);

  const load = async (list) => {
    const f = list[0];
    if (!f || (f.type !== "application/pdf" && !f.name.toLowerCase().endsWith(".pdf"))) { toast.error("Please choose a PDF file."); return; }
    setBusy(true);
    try {
      const bytes = new Uint8Array(await f.arrayBuffer());
      const doc = await pdfjs.getDocument({ data: bytes.slice() }).promise;
      const out = [];
      for (let i = 1; i <= doc.numPages; i++) {
        const page = await doc.getPage(i);
        const viewport = page.getViewport({ scale: 0.4 });
        const canvas = document.createElement("canvas");
        canvas.width = viewport.width; canvas.height = viewport.height;
        await page.render({ canvasContext: canvas.getContext("2d"), viewport }).promise;
        out.push({ orig: i - 1, rotation: 0, thumb: canvas.toDataURL("image/jpeg", 0.7) });
      }
      setSrcBytes(bytes);
      setPages(out);
      setFileMeta({ name: f.name, size: f.size, pageCount: doc.numPages });
    } catch (e) {
      toast.error("Could not read this PDF (it may be password-protected).");
    } finally { setBusy(false); }
  };

  const move = (i, dir) => setPages((p) => { const n = [...p]; const j = i + dir; if (j < 0 || j >= n.length) return p; [n[i], n[j]] = [n[j], n[i]]; return n; });
  const del = (i) => setPages((p) => p.filter((_, x) => x !== i));
  const rotate = (i) => setPages((p) => p.map((pg, x) => x === i ? { ...pg, rotation: (pg.rotation + 90) % 360 } : pg));

  const build = async () => {
    if (!pages.length) { toast.error("No pages left to save."); return; }
    setBusy(true);
    try {
      const src = await PDFDocument.load(srcBytes.slice());
      const out = await PDFDocument.create();
      const copied = await out.copyPages(src, pages.map((p) => p.orig));
      copied.forEach((pg, idx) => {
        const base = pg.getRotation().angle;
        pg.setRotation(degrees((base + pages[idx].rotation) % 360));
        out.addPage(pg);
      });
      const bytes = await out.save();
      const url = URL.createObjectURL(new Blob([bytes], { type: "application/pdf" }));
      const a = document.createElement("a"); a.href = url; a.download = "ecroply-rearranged.pdf"; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 4000);
      toast.success("Rearranged PDF downloaded!");
    } catch (e) { toast.error("Could not build the PDF."); }
    finally { setBusy(false); }
  };

  const reset = () => { setSrcBytes(null); setPages([]); setFileMeta(null); };

  return (
    <>
      <Seo title="Rearrange PDF — Reorder, Delete & Rotate Pages | Ecroply AI"
        description="Reorder, delete and rotate PDF pages visually, then download. Runs in your browser, no upload, no login."
        path="/tools/rearrange-pdf" />
      <GenericToolShell icon="MoveVertical" title="Rearrange PDF" promise="Reorder, delete and rotate PDF pages, then download."
        privacy="Your PDF is processed in your browser on this device and is not uploaded to a server.">
        {!srcBytes ? (
          <FileDrop files={fileMeta ? [fileMeta] : []} onAdd={load} onRemove={reset} multiple={false} hint="Choose a single PDF" />
        ) : (
          <div>
            <div className="flex items-center justify-between mb-4">
              <p className="text-sm text-slate-600">{pages.length} page{pages.length === 1 ? "" : "s"}</p>
              <Button variant="ghost" size="sm" onClick={reset} data-testid="rearrange-reset">Choose another PDF</Button>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4" data-testid="page-grid">
              {pages.map((p, i) => (
                <div key={i} className="border border-slate-200 rounded-xl overflow-hidden bg-slate-50" data-testid={`page-thumb-${i}`}>
                  <div className="aspect-[3/4] flex items-center justify-center bg-white overflow-hidden">
                    <img src={p.thumb} alt={`Page ${i + 1}`} className="max-h-full max-w-full transition-transform" style={{ transform: `rotate(${p.rotation}deg)` }} />
                  </div>
                  <div className="flex items-center justify-between px-2 py-1.5 bg-slate-100">
                    <span className="text-xs font-semibold text-slate-500">{i + 1}</span>
                    <div className="flex gap-0.5">
                      <button onClick={() => move(i, -1)} className="p-1 text-slate-500 hover:text-blue-600" data-testid={`page-left-${i}`}><ArrowLeft className="w-3.5 h-3.5" /></button>
                      <button onClick={() => move(i, 1)} className="p-1 text-slate-500 hover:text-blue-600" data-testid={`page-right-${i}`}><ArrowRight className="w-3.5 h-3.5" /></button>
                      <button onClick={() => rotate(i)} className="p-1 text-slate-500 hover:text-blue-600" data-testid={`page-rotate-${i}`}><RotateCw className="w-3.5 h-3.5" /></button>
                      <button onClick={() => del(i)} className="p-1 text-slate-500 hover:text-red-600" data-testid={`page-delete-${i}`}><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <Button onClick={build} disabled={busy || !pages.length} className="w-full mt-6 h-12 bg-blue-600 hover:bg-blue-700" data-testid="rearrange-download">
              {busy ? <><Loader2 className="w-5 h-5 animate-spin mr-2" /> Building…</> : <><Download className="w-5 h-5 mr-2" /> Download rearranged PDF</>}
            </Button>
          </div>
        )}
        {busy && !srcBytes && <p className="text-center text-sm text-slate-500 mt-4"><Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Rendering pages…</p>}
      </GenericToolShell>
    </>
  );
}
